import { DateTime } from 'luxon';
import us from 'us';
import { defaults } from 'shared-assets/lib/colors';
import {
  MedicalHistoryCategory,
  SocialHistoryCategory,
} from '../scenes/MedicalHistory/HistorySectionData';

export function basicSearch(query, items) {
  return items
    .filter(i => !query || i.name.toLowerCase().includes(query.toLowerCase()))
    .splice(0, 10);
}

export const colors = {
  link: '#0052cc',
  subtle: 'rgba(69, 72, 89, 0.75)',
  base: '#454859',
  // status
  success: `var(--success-color, ${defaults.successColor})`,
  error: `var(--error-color, ${defaults.errorColor})`,
  warning: `var(--warning-color, ${defaults.warningColor})`,
  info: `var(--data-vis-info-color, ${defaults.primaryColor})`,
};

const medFormsList = {
  'MEDICATIONS_&_ALLERGIES': '-15',
  PAST_MEDICAL_HISTORY: '-16',
  FAMILY_HISTORY: '-17',
  SOCIAL_HISTORY: '-18',
  SURGICAL_HISTORY: '-19',
  'HOSPITALIZATIONS/PROCEDURES': '-20',
  IMPLANTABLE_DEVICES: '-21',
  'OB/GYN_HISTORY': '-22',
};

export function grouper({ items = {}, category }) {
  let html = '';
  let groupedHistory = [];
  Object.entries(items).forEach(([key, value]) => {
    if (value) {
      const categoryId = parseInt(key.split(':')[0]);
      const item = key.split('checkbox-')[1];

      if (!groupedHistory[categoryId]) {
        groupedHistory[categoryId] = [item];
      } else {
        groupedHistory[categoryId].push(item);
      }
    }
  });
  groupedHistory.forEach((title, index) => {
    html += `<strong>${category.properties[index].name}</strong><br/>`;
    title.forEach(item => {
      html += item;
      html += '<br />';
    });
  });
  return html;
}

export function formatIntake(
  intakeSource,
  activeSections,
  {
    medications,
    allergies,
    pastMedicalHistory = {},
    familyHistory = {},
    socialHistory = {},
    surgicalHistory = {},
    hospitalizationHistory = {},
    implantableDeviceHistory = {},
    obgyn = {},
    screeningHtmls = {},
  } = {},
) {
  let intake = JSON.parse(JSON.stringify(intakeSource)); // deep copy
  // Generate the pharmacy id's
  if (intakeSource.pharmacies) {
    intake.pharmacyIds = Array.from(intakeSource.pharmacies.values())
      .map(JSON.parse)
      .map(pharmacy => pharmacy.pharmacyId);
  }

  // Convert date of birth's to Kareo format
  if (intake.emergencyContact && intake.emergencyContact.dateOfBirth) {
    intake.emergencyContact.dateOfBirth = DateTime.fromFormat(
      intake.emergencyContact.dateOfBirth,
      'LL/dd/yyyy',
      { zone: 'utc' },
    ).ts;
  }
  if (intake.patientContact && intake.patientContact.dateOfBirth) {
    var minDate = DateTime.fromFormat('01/01/1900', 'LL/dd/yyyy', {
      zone: 'utc',
    });

    var formattedDateTime = DateTime.fromFormat(
      intake.patientContact.dateOfBirth,
      'LL/dd/yyyy',
      { zone: 'utc' },
    );

    // If the date is not a valid DOB, don't add entered date to payload.
    intake.patientContact.dateOfBirth =
      formattedDateTime.isValid && formattedDateTime.ts > minDate.ts
        ? formattedDateTime.ts
        : '';
  }
  if (
    intake.responsiblePartyContact &&
    intake.responsiblePartyContact.dateOfBirth
  ) {
    intake.responsiblePartyContact.dateOfBirth = DateTime.fromFormat(
      intake.responsiblePartyContact.dateOfBirth,
      'LL/dd/yyyy',
      { zone: 'utc' },
    ).ts;
  }
  if (
    intake.primaryInsurance &&
    intake.primaryInsurance.holderContact &&
    intake.primaryInsurance.holderContact.dateOfBirth
  ) {
    intake.primaryInsurance.holderContact.dateOfBirth = DateTime.fromFormat(
      intake.primaryInsurance.holderContact.dateOfBirth,
      'LL/dd/yyyy',
      { zone: 'utc' },
    ).ts;
  }
  if (
    intake.secondaryInsurance &&
    intake.secondaryInsurance.holderContact &&
    intake.secondaryInsurance.holderContact.dateOfBirth
  ) {
    intake.secondaryInsurance.holderContact.dateOfBirth = DateTime.fromFormat(
      intake.secondaryInsurance.holderContact.dateOfBirth,
      'LL/dd/yyyy',
      { zone: 'utc' },
    ).ts;
  }

  // Remove dashes from social security numbers
  if (intake.patientContact && intake.patientContact.ssn) {
    intake.patientContact.ssn = intake.patientContact.ssn
      .replace(/-|_/g, '')
      .trim();
  }
  if (intake.responsiblePartyContact && intake.responsiblePartyContact.ssn) {
    intake.responsiblePartyContact.ssn = intake.responsiblePartyContact.ssn
      .replace(/-|_/g, '')
      .trim();
  }
  if (
    intake.primaryInsurance &&
    intake.primaryInsurance.holderContact &&
    intake.primaryInsurance.holderContact.ssn
  ) {
    intake.primaryInsurance.holderContact.ssn = intake.primaryInsurance.holderContact.ssn
      .replace(/-|_/g, '')
      .trim();
  }
  if (
    intake.secondaryInsurance &&
    intake.secondaryInsurance.holderContact &&
    intake.secondaryInsurance.holderContact.ssn
  ) {
    intake.secondaryInsurance.holderContact.ssn = intake.secondaryInsurance.holderContact.ssn
      .replace(/-|_/g, '')
      .trim();
  }

  // Remove all misc characters from phone numbers
  if (intake.emergencyContact && intake.emergencyContact.phone) {
    intake.emergencyContact.phone = intake.emergencyContact.phone.replace(
      /-|\s+|\(|\)/g,
      '',
    );
  }
  if (intake.patientContact && intake.patientContact.phone) {
    intake.patientContact.phone = intake.patientContact.phone.replace(
      /-|\s+|\(|\)/g,
      '',
    );
  }
  if (intake.primaryInsurance && intake.primaryInsurance.phoneNumber) {
    intake.primaryInsurance.phoneNumber = intake.primaryInsurance.phoneNumber.replace(
      /-|\s+|\(|\)/g,
      '',
    );
  }
  if (intake.secondaryInsurance && intake.secondaryInsurance.phoneNumber) {
    intake.secondaryInsurance.phoneNumber = intake.secondaryInsurance.phoneNumber.replace(
      /-|\s+|\(|\)/g,
      '',
    );
  }
  if (intake.responsiblePartyContact && intake.responsiblePartyContact.phone) {
    intake.responsiblePartyContact.phone = intake.responsiblePartyContact.phone.replace(
      /-|\s+|\(|\)/g,
      '',
    );
  }

  if (intake.methodOfPayment === 'selfPay') {
    ['primaryInsurance', 'secondaryInsurance'].forEach(item => {
      intake[item].addressToSendClaimsTo = {
        addressLine1: null,
        addressLine2: null,
        city: null,
        stateId: null,
        zipCode: null,
      };
      intake[item].groupNumber = null;
      intake[item].holderContact = {
        address: null,
        dateOfBirth: null,
        email: null,
        firstName: null,
        lastName: null,
        maidenLastName: null,
        middleName: null,
        phone: null,
        phoneExt: null,
        phoneType: null,
        sex: null,
        ssn: null,
        suffix: null,
      };
      intake[item].holderPolicyNumber = null;
      intake[item].insuranceCompanyName = null;
      intake[item].insuranceCompanyPlanName = null;
      intake[item].insuranceDeclinedReason = null;
      intake[item].phoneNumber = null;
      intake[item].policyNumber = null;
      intake[item].relationshipToPolicyHolder = null;
    });
  }

  // Remove empty ssn's
  if ((intake.patientContact.ssn || '').length !== 9) {
    delete intake.patientContact.ssn;
  }
  if ((intake.responsiblePartyContact.ssn || '').length !== 9) {
    delete intake.responsiblePartyContact.ssn;
  }

  // Remove unneeded data
  delete intake.financiallyResponsibleParty;
  delete intake.isSecondaryInsuranceSectionVisible;
  delete intake.pharmacies;

  if (!activeSections.includes('basic-information')) {
    intake.patientContact = null;
  }
  if (!activeSections.includes('emergency-contact')) {
    intake.emergencyContact = null;
  }
  if (!activeSections.includes('financial-information')) {
    intake.responsiblePartyContact = null;
    intake.primaryInsurance = null;
    intake.secondaryInsurance = null;
  }
  if (!activeSections.includes('ob-gyn-screen')) {
    intake.obgyn = null;
  }

  // Build up the html intake doc
  intake.forms = intake.forms || [];
  let medicalForm = intake.forms.find(form => form.formId === -1);
  // Create the medical form for new users in the section-selection feature toggle
  if (
    !medicalForm &&
    !!intake.forms.find(form => form.formId < -14 && form.formId > -23)
  ) {
    medicalForm = {
      formId: -1,
      displayDescription: null,
      formTitle: 'Medication, Allergy & History',
      internalDisplayName: 'Medication, Allergy & History',
      updated: Date.now(),
      fields: [{}, {}],
    };
    intake.forms.push(medicalForm);
  }

  if (medicalForm) {
    // Meds & allergies
    medicalForm.fields[0].data = {};
    if (medications) {
      medicalForm.fields[0].data.value = `<h3>Medications</h3>${medications}`;
    }
    if (allergies) {
      medicalForm.fields[0].data.value += `<h3>Allergies</h3>${allergies}`;
    }

    // Past medical history
    const medicalHistories = grouper({
      items: (pastMedicalHistory || {}).items,
      category: MedicalHistoryCategory,
    });
    const pastMedicalHistoryText =
      (medicalHistories || (pastMedicalHistory || {}).comments) &&
      `
      <h3>Medical History</h3>
      ${medicalHistories}
      ${
        pastMedicalHistory.comments
          ? `<br/><strong>Other comments</strong><br/>${pastMedicalHistory.comments}`
          : ''
      }
    `;
    // append pastMedicalHistory checkboxed values to payload
    const { items } = pastMedicalHistory;
    if (items && Object.keys(items).length > 0) {
      // typical key,value pairs: "10:checkbox-Type1-DM": false, "10:checkbox-Goiter": true, "13:checkbox-MS-Injury": true
      const payloadToAppend = Object.keys(items)
        .filter(key => items[key])
        .map(key => {
          return {
            formCaptureId: null,
            formConfigId: medFormsList.PAST_MEDICAL_HISTORY,
            hxFormId: null,
            formName: key.split('checkbox-')[1],
          };
        });
      intake.formCaptureEntityDataList = payloadToAppend;
    }

    // Family history
    const familyHistories =
      familyHistory.items &&
      familyHistory.items.reduce((accumulatedText, history) => {
        if (history.member || history.status || history.diseases) {
          accumulatedText += `<strong>${history.member} (${history.status})</strong><p>Diseases: ${history.diseases}</p>`;
        }
        return accumulatedText;
      }, '');
    const familyHistoryText =
      (familyHistories || familyHistory.isUnknown || familyHistory.comments) &&
      `
      <h3>Family History</h3>
      ${familyHistories}
      ${
        familyHistory.isUnknown
          ? '<br/><strong>Family history is unknown</strong><br/>'
          : ''
      }
      ${
        familyHistory.comments
          ? `<br/><strong>Other comments</strong><br/>${familyHistory.comments}`
          : ''
      }
  `;

    // Social history
    const socialHistories = grouper({
      items: socialHistory.items,
      category: SocialHistoryCategory,
    });
    const socialHistoryText =
      (socialHistories ||
        socialHistory.hasTraveled ||
        socialHistory.comments) &&
      `
      <h3>Social History</h3>
      ${socialHistories}
      ${
        socialHistory.hasTraveled
          ? '<br/><strong>Has traveled domestically or internationally in the past 6 months</strong><br/>'
          : ''
      }
      ${
        socialHistory.comments
          ? `<br/><strong>Other comments</strong><br/>${socialHistory.comments}`
          : ''
      }
    `;

    // Surgical history
    const surgicalHistories = surgicalHistory.items
      ? Object.entries(surgicalHistory.items).reduce((acc, [key, value]) => {
          if (value) {
            acc += key.split('checkbox-')[1].replace(/-/, ' ');
            acc += '<br/>';
          }
          return acc;
        }, '')
      : '';
    const surgicalHistoryText =
      (surgicalHistories || surgicalHistory.comments) &&
      `
      <h3>Surgical History</h3>
      ${surgicalHistories}
      ${
        surgicalHistory.comments
          ? `<br/><strong>Other comments:</strong><br/>${surgicalHistory.comments}`
          : ''
      }
    `;

    // Hospitalization history
    const hospitalizationHistories = hospitalizationHistory.items
      ? hospitalizationHistory.items.reduce((accumulatedText, history) => {
          if (history.procedure || history.admissionDate || history.reason) {
            accumulatedText += `<strong>Procedure: ${history.procedure} (${history.admissionDate})</strong><p>Reason: ${history.reason}</p>`;
          }
          return accumulatedText;
        }, '')
      : '';
    const hospitalizationHistoryText =
      hospitalizationHistories &&
      `
      <h3>Hospitalization History</h3>
      ${hospitalizationHistories}
    `;

    // Implantable Device History
    const implantableDeviceHistories = (
      implantableDeviceHistory.items || []
    ).reduce((accumulatedText, history) => {
      if (
        history.UDI ||
        history.unknownUDI ||
        history.type ||
        history.comments
      ) {
        const udiConcat = `${history.UDI} ${
          history.unknownUDI && 'Unknown UDI'
        }`;
        accumulatedText += `<strong>Device Type: ${
          history.type
        } (UDI: ${udiConcat.trim()})</strong><p>Device comments: ${
          history.comments
        }</p>`;
      }
      return accumulatedText;
    }, '');
    const implantableDeviceHistoryText =
      implantableDeviceHistories &&
      `
      <h3>Implantable Device History</h3>
      ${implantableDeviceHistories}
    `;

    // OBGYN history
    let obgynText = '';
    if (activeSections.includes('ob-gyn-screen')) {
      obgynText = '';
      obgynText += `
        <h3>OB/GYN History</h3>
        <h4>Gynecology</h4>
        <p>Age onset of menstruation: ${obgyn.ageOnset || ''}</p>
        <p>Age of menopause: ${obgyn.ageMenopause || ''}</p>
      `;

      if (obgyn.hormoneReplacement) {
        obgynText += '<p>History of hormone replacement therapy</p>';
      }
      if (obgyn.abnormalPap) {
        obgynText += '<p>History of abnormal PAP smear</p>';
      }
      if (obgyn.cervicalBiopsy) {
        obgynText += '<p>History of cervical biopsy</p>';
      }
      if (obgyn.fertilityDrugs) {
        obgynText += '<p>History of fertility drugs</p>';
      }
      if (obgyn.irregularMenses) {
        obgynText += '<p>History of irregular menses</p>';
      }

      obgynText += `
        <h4>Pregnancy</h4>
        <p>Total pregnancies: ${obgyn.totalPregnancies || ''}</p>
        <p>Full term: ${obgyn.fullTerm || ''}</p>
        <p>Pre term: ${obgyn.preTerm || ''}</p>
        <p>Miscarriages: ${obgyn.miscarriages || ''}</p>
        <p>Living: ${obgyn.living || ''}</p>
        <h4>Comments</h4>
        <p>${obgyn.comments || ''}</p>
      `;
    }

    // Construct payload
    medicalForm.fields[1].data = {};
    medicalForm.fields[1].data.value = `
    ${pastMedicalHistoryText}
    ${familyHistoryText}
    ${socialHistoryText}
    ${surgicalHistoryText}
    ${hospitalizationHistoryText}
    ${implantableDeviceHistoryText}
    ${obgynText}`;

    if (
      !pastMedicalHistoryText &&
      !familyHistoryText &&
      !socialHistoryText &&
      !surgicalHistoryText &&
      !hospitalizationHistoryText &&
      !implantableDeviceHistoryText &&
      !obgynText
    ) {
      medicalForm.fields[1].data.value = '<h3>Medical History</h3>No responses';
    }

    if (medicalForm.fields[0].data.value) {
      medicalForm.fields[0].data.value.replace(/(?:\r\n)/g, '<br/><br/>');
      medicalForm.fields[0].data.value.replace(/(?:\r|\n)/g, '<br/>');
    }
    if (medicalForm.fields[1].data.value) {
      medicalForm.fields[1].data.value.replace(/(?:\r\n)/g, '<br/><br/>');
      medicalForm.fields[1].data.value.replace(/(?:\r|\n)/g, '<br/>');
    }

    // Backend needs these in place
    medicalForm.fields[0].order = 1;
    medicalForm.fields[0].fieldType = 'input';
    medicalForm.fields[0].properties = {
      labelText: 'Medications & Allergies',
    };
    medicalForm.fields[1].order = 2;
    medicalForm.fields[1].fieldType = 'input';
    medicalForm.fields[1].properties = {
      labelText: 'Medical History',
    };
  }

  const formKeys = Object.keys(screeningHtmls);
  if (formKeys.length > 0) {
    formKeys.forEach(key => {
      let screeningForm = intake.forms.find(
        form => form.internalDisplayName === key,
      );
      if (screeningForm) {
        screeningForm.fields = [
          {
            data: {
              value: screeningHtmls[key],
            },
            order: 1,
            fieldType: 'htmlInput',
            properties: { labelText: key },
          },
        ];
      }
    });
  }

  let covid19Form = intake.forms.find(
    form => form.internalDisplayName === 'COVID-19',
  );
  if (covid19Form) {
    covid19Form.fields = [
      {
        data: {
          value: intake.covid19Html,
        },
        order: 1,
        fieldType: 'htmlInput',
        properties: { labelText: 'COVID-19' },
      },
    ];
  }

  return intake;
}

export function formatServiceLocationAddress(serviceLocation, staticData) {
  // clearly show we support empty location objects
  let emptyObjCheck = Object.values(serviceLocation).every(
    value => value === null || value === 0,
  );

  if (emptyObjCheck) {
    return '';
  }

  const serviceLocationState = staticData.states.find(
    element => element.id === serviceLocation.stateId,
  );

  let state;

  if (!!serviceLocationState) {
    state = us.lookup(serviceLocationState.name)
      ? us.lookup(serviceLocationState.name).abbr
      : serviceLocationState.name;
  } else {
    state = '';
  }

  let address = [];

  // EN-12700: Display what we have for the address, presumably the patient has enough context to fill in the blanks (ie. if state is missing)
  if (!!serviceLocation.address1) {
    address.push(serviceLocation.address1);
  }

  if (!!serviceLocation.city) {
    address.push(serviceLocation.city);
  }

  if (!!state) {
    address.push(`${state}`);
  }

  let formattedAddress = address.join(', ');

  if (!!serviceLocation.zip) {
    formattedAddress = `${formattedAddress} ${serviceLocation.zip.substring(
      0,
      5,
    )}`;
  }

  return formattedAddress;
}
